import React, { useState, useEffect } from 'react';
import { format, subDays, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { FaTimes, FaTrash, FaList, FaTh, FaTag, FaPencilAlt, FaCopy, FaInfoCircle } from 'react-icons/fa';
import CustomCalendar from '../components/CustomCalendar';
import Header from '../components/Header';
import StickyNote from '../components/StickyNote';
import ExpenseModal from '../components/ExpenseModal';
import { useEffectiveUser } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import PieChartStructure from '../components/PieChartStructure';
import BarChartStructure from '../components/BarChartStructure';
import DataTableStructure from '../components/DataTableStructure';
import DeleteModal from '../components/DeleteModal';
import { Tooltip } from 'react-tooltip';
import { CURRENCY_SYMBOLS_TEXT } from '../utils/currencySymbols'
import { motion } from 'framer-motion';
import { preparePendingExpenses } from '../components/PreparePendingExpenses';
import AllTimeExpenseView from '../components/AllTimeExpenseView';
import CategorySummaryTables from '../components/CategorySummaryTables';

const Expense = () => {
  const [tabs, setTabs] = useState([
    { id: 'overview', title: 'Overview', isOverview: true }
  ]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expenses, setExpenses] = useState({});
  const [categories, setCategories] = useState([]);
  const { currentUser } = useEffectiveUser();
  const [userCurrencies, setUserCurrencies] = useState(['GBP']); // Default currency
  const [editingExpense, setEditingExpense] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [sortOption, setSortOption] = useState('default');
  const [tableGlobalFilter, setTableGlobalFilter] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(true);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [expenseTableSortBy, setExpenseTableSortBy] = useState([]);
  const [pendingTableSortBy, setPendingTableSortBy] = useState([]);
  const [displayMode, setDisplayMode] = useState(() => {
    const savedMode = localStorage.getItem('expenseDisplayMode');
    return savedMode || 'grid'; // default to 'grid' if no saved preference
  });
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [isPendingExpanded, setIsPendingExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('monthly'); // Add new state for view mode

  // Add this new useEffect to handle saving display mode changes
  useEffect(() => {
    localStorage.setItem('expenseDisplayMode', displayMode);
  }, [displayMode]);

  // Updated stickyColors array with more colours
  const stickyColors = [
    'bg-yellow-200', 'bg-green-200', 'bg-blue-200', 
    'bg-pink-200', 'bg-purple-200', 'bg-orange-200',
    'bg-red-200', 'bg-teal-200', 'bg-indigo-200', 
    'bg-cyan-200', 'bg-lime-200', 'bg-rose-200'
  ];

  // Fetch user data and expenses when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Fetch settings
        const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
        if (settingsDoc.exists()) {
          const { currencies } = settingsDoc.data();
          setUserCurrencies(currencies);
        }

        // Initialize or fetch categories
        const categoriesRef = doc(db, 'categories', currentUser.uid);
        const categoriesDoc = await getDoc(categoriesRef);
        if (categoriesDoc.exists()) {
          setCategories(categoriesDoc.data().items || []);
        } else {
          // Initialize empty categories array if none exists
          await setDoc(categoriesRef, { items: [] });
          setCategories([]);
        }

        // Initialize or fetch expenses
        const expensesRef = doc(db, 'expenses', currentUser.uid);
        const expensesDoc = await getDoc(expensesRef);
        if (expensesDoc.exists()) {
          const existingExpenses = expensesDoc.data().items || {};
          setExpenses(existingExpenses);
          
          // Process recurring expenses after loading existing ones
          setTimeout(() => {
            processDueRecurringExpenses();
          }, 0);
        } else {
          // Initialize empty expenses object if none exists
          await setDoc(expensesRef, { items: {} });
          setExpenses({});
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load data');
      }
    };

    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser]);

  // Define fetchRecurringExpenses before using it in useEffect
  const fetchRecurringExpenses = async () => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'recurringExpenses'),
      where('userId', '==', currentUser.uid)
    );
    try {
      const querySnapshot = await getDocs(q);
      const expenses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecurringExpenses(expenses.filter(exp => !exp.isPaused));
    } catch (error) {
      console.error('Error fetching recurring expenses:', error);
    }
  };

  // Update this useEffect to properly handle recurring expenses
  useEffect(() => {
    fetchRecurringExpenses();
  }, [currentUser]);

  // Add this new function near the top of the component
  const getNextAvailableColor = (existingExpenses, stickyColors) => {
    if (!existingExpenses || existingExpenses.length === 0) {
      return stickyColors[0];
    }

    // Get colors already used by existing expenses
    const usedColors = existingExpenses.map(expense => expense.color);

    // Find first unused color
    const availableColor = stickyColors.find(color => !usedColors.includes(color));

    // If all colors are used, find the least used color
    if (!availableColor) {
      const colorCounts = usedColors.reduce((acc, color) => {
        acc[color] = (acc[color] || 0) + 1;
        return acc;
      }, {});
      
      return Object.entries(colorCounts)
        .sort(([, countA], [, countB]) => countA - countB)[0][0];
    }

    return availableColor;
  };

  // Helper function to check if a year is a leap year
  const isLeapYear = (year) => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  };

  // Update processDueRecurringExpenses to merge with existing expenses
  const processDueRecurringExpenses = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const dueExpenses = recurringExpenses.filter(exp => {
        if (exp.isPaused || exp.isCompleted) return false;
        if (exp.nextPaymentDate === "COMPLETED") return false;
        
        const nextPayment = new Date(exp.nextPaymentDate);
        nextPayment.setHours(0, 0, 0, 0);
        return nextPayment <= today;
      });

      if (dueExpenses.length === 0) return;

      // Fetch current expenses to ensure we have the latest data
      const expensesRef = doc(db, 'expenses', currentUser.uid);
      const expensesDoc = await getDoc(expensesRef);
      const currentExpenses = expensesDoc.exists() ? expensesDoc.data().items : {};
      
      const updatedExpenses = { ...currentExpenses };
      const batch = [];

      for (const dueExpense of dueExpenses) {
        const dateKey = format(new Date(dueExpense.nextPaymentDate), 'yyyy-MM-dd');
        
        // Initialize the date array if it doesn't exist
        if (!updatedExpenses[dateKey]) {
          updatedExpenses[dateKey] = [];
        }

        // Check if this recurring expense has already been processed for this date
        const alreadyProcessed = updatedExpenses[dateKey].some(
          exp => exp.fromRecurring === dueExpense.id
        );

        if (!alreadyProcessed) {
          // Create the new expense entry with distributed color
          const newExpense = {
            id: `${dueExpense.id}-${Date.now()}`,
            title: dueExpense.title,
            amount: dueExpense.amount,
            currency: dueExpense.currency,
            category: dueExpense.category,
            color: getNextAvailableColor(updatedExpenses[dateKey], stickyColors),
            createdAt: new Date().toISOString(),
            fromRecurring: dueExpense.id
          };

          updatedExpenses[dateKey].push(newExpense);

          // For one-off payments, mark them as paused after processing
          if (dueExpense.isOneOff) {
            batch.push({
              id: dueExpense.id,
              isPaused: true  // Mark one-off payment as paused after processing
            });
          } else {
            // Calculate next payment date for recurring expenses
            const nextDate = new Date(dueExpense.nextPaymentDate);
            const normalizedUnit = dueExpense.period?.unit?.toLowerCase().replace(/s$/, '') || 'day';
            const periodNumber = parseInt(dueExpense.period?.number) || 1;
            
            // Validate the initial date
            if (isNaN(nextDate.getTime())) {
              console.error('Invalid nextPaymentDate for expense:', dueExpense.id, dueExpense.nextPaymentDate);
              continue; // Skip this expense if the date is invalid
            }
            
            // Validate period data
            if (!dueExpense.period || isNaN(periodNumber) || periodNumber <= 0) {
              console.error('Invalid period data for expense:', dueExpense.id, 'Period:', dueExpense.period);
              continue; // Skip this expense if period data is invalid
            }
            
            // Use more robust date arithmetic to prevent invalid dates
            try {
              switch (normalizedUnit) {
                case 'day':
                  nextDate.setUTCDate(nextDate.getUTCDate() + periodNumber);
                  break;
                case 'week':
                  nextDate.setUTCDate(nextDate.getUTCDate() + (periodNumber * 7));
                  break;
                case 'month':
                  // Handle month addition more carefully to avoid invalid dates
                  const currentMonth = nextDate.getUTCMonth();
                  const currentDay = nextDate.getUTCDate();
                  nextDate.setUTCMonth(currentMonth + periodNumber);
                  
                  // If the day changed (e.g., Jan 31 + 1 month = Mar 3), adjust to last day of target month
                  if (nextDate.getUTCDate() !== currentDay) {
                    nextDate.setUTCDate(0); // Set to last day of previous month
                  }
                  break;
                case 'year':
                  // Handle leap year edge cases
                  const currentYear = nextDate.getUTCFullYear();
                  const currentMonthYear = nextDate.getUTCMonth();
                  const currentDayYear = nextDate.getUTCDate();
                  nextDate.setUTCFullYear(currentYear + periodNumber);
                  
                  // Handle Feb 29 on non-leap years
                  if (currentMonthYear === 1 && currentDayYear === 29 && !isLeapYear(nextDate.getUTCFullYear())) {
                    nextDate.setUTCDate(28);
                  }
                  break;
                default:
                  console.warn('Unknown period unit for expense:', dueExpense.id, 'Unit:', normalizedUnit);
                  // Default to day if unknown unit
                  nextDate.setUTCDate(nextDate.getUTCDate() + periodNumber);
                  break;
              }
            } catch (error) {
              console.error('Error calculating next date for expense:', dueExpense.id, 'Error:', error);
              continue;
            }
            
            // Validate the calculated date before using it
            if (isNaN(nextDate.getTime())) {
              console.error('Invalid calculated nextDate for expense:', dueExpense.id, 'Original date:', dueExpense.nextPaymentDate, 'Period:', dueExpense.period);
              continue; // Skip this expense if the calculated date is invalid
            }
            
            // Check if there's an end date and if the next payment exceeds it
            const isCompleted = dueExpense.endDate && new Date(nextDate) > new Date(dueExpense.endDate);
            
            // If the payment cycle is completed (next payment would be after end date)
            if (isCompleted) {
              batch.push({
                id: dueExpense.id,
                nextPaymentDate: "COMPLETED",
                isCompleted: true
              });
            } else {
              batch.push({
                id: dueExpense.id,
                nextPaymentDate: nextDate.toISOString()
              });
            }
          }
        }
      }

      // Only update if there are changes
      if (batch.length > 0) {
        // Update Firebase with new expenses
        await updateDoc(doc(db, 'expenses', currentUser.uid), {
          items: updatedExpenses
        });

        // Update all recurring expenses
        for (const update of batch) {
          await updateDoc(doc(db, 'recurringExpenses', update.id), update);
        }

        // Update local state
        setExpenses(updatedExpenses);
        // Refresh recurring expenses
        fetchRecurringExpenses();
      }
    } catch (error) {
      console.error('Error processing recurring expenses:', error);
      toast.error('Failed to process recurring expenses');
    }
  };

  // Update this useEffect to run more frequently
  useEffect(() => {
    if (currentUser) {
      processDueRecurringExpenses();
    }
  }, [currentUser, currentDate, recurringExpenses]); // Add recurringExpenses as a dependency

  // Function to update categories in Firebase
  const updateCategories = async (newCategories) => {
    try {
      await setDoc(doc(db, 'categories', currentUser.uid), {
        items: newCategories
      });
      setCategories(newCategories);
    } catch (error) {
      console.error('Error updating categories:', error);
      toast.error('Failed to save category');
    }
  };

  const handleDateClick = async (date) => {
    // First process any due recurring expenses
    await processDueRecurringExpenses();
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const formattedDate = format(date, 'd MMM yyyy');
    
    // Check if we already have a tab for this date
    if (tabs.some(tab => tab.date === dateStr)) {
      setActiveTab(dateStr);
      return;
    }

    // Check if we've reached the maximum number of tabs (6 including Overview)
    if (tabs.length >= 6) {
      toast.info('Maximum number of tabs reached. Please close some tabs first.');
      return;
    }

    // Add new tab
    const newTab = {
      id: dateStr,
      title: formattedDate,
      date: dateStr,
      isOverview: false
    };

    setTabs([...tabs, newTab]);
    setActiveTab(dateStr);
  };

  const closeTab = (tabId, event) => {
    event.stopPropagation();
    
    // If closing active tab, switch to overview
    if (activeTab === tabId) {
      setActiveTab('overview');
    }
    
    setTabs(tabs.filter(tab => tab.id !== tabId));
  };

  // Update handleDeleteExpense to work with both table and sticky notes
  const handleDeleteExpense = async (expense) => {
    try {
      let expenseId = typeof expense === 'string' ? expense : expense.id;
      let dateKey = null;
      let expenseToDelete = null;

      // Search through all dates to find the expense
      for (const [date, dayExpenses] of Object.entries(expenses)) {
        const found = dayExpenses.find(exp => exp.id === expenseId);
        if (found) {
          dateKey = date;
          expenseToDelete = found;
          break;
        }
      }

      if (!dateKey || !expenseToDelete) {
        throw new Error('Expense not found');
      }

      // Filter out the expense using the ID
      const updatedTabExpenses = expenses[dateKey].filter(exp => exp.id !== expenseId);
      
      const updatedExpenses = {
        ...expenses,
        [dateKey]: updatedTabExpenses
      };

      // Remove the date key if no expenses remain
      if (updatedExpenses[dateKey].length === 0) {
        delete updatedExpenses[dateKey];
      }

      // Update Firebase
      await setDoc(doc(db, 'expenses', currentUser.uid), {
        items: updatedExpenses
      });

      // Update local state
      setExpenses(updatedExpenses);
      toast.success('Expense deleted successfully');
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    } finally {
      setDeleteModalOpen(false);
      setExpenseToDelete(null);
    }
  };

  // Modify handleEditExpense to work with both table and sticky notes
  const handleEditExpense = async (updatedExpense) => {
    try {
      // Find the date that contains this expense
      const oldDateKey = Object.entries(expenses).find(([date, dayExpenses]) => 
        dayExpenses.some(exp => exp.id === updatedExpense.id)
      )?.[0];

      if (!oldDateKey) {
        throw new Error('Original expense not found');
      }

      const newDateKey = updatedExpense.date;
      const updatedExpenses = { ...expenses };

      // Find the original expense to get its color
      const originalExpense = updatedExpenses[oldDateKey].find(
        exp => exp.id === updatedExpense.id
      );

      // Remove expense from old date array
      updatedExpenses[oldDateKey] = updatedExpenses[oldDateKey].filter(
        exp => exp.id !== updatedExpense.id
      );

      // Remove the date key if no expenses remain
      if (updatedExpenses[oldDateKey].length === 0) {
        delete updatedExpenses[oldDateKey];
      }

      // Initialize the new date array if it doesn't exist
      if (!updatedExpenses[newDateKey]) {
        updatedExpenses[newDateKey] = [];
      }

      // Add expense to new date
      updatedExpenses[newDateKey] = [
        ...updatedExpenses[newDateKey],
        {
          ...updatedExpense,
          color: originalExpense.color,
          updatedAt: new Date().toISOString()
        }
      ];

      // Update Firebase
      await updateDoc(doc(db, 'expenses', currentUser.uid), {
        items: updatedExpenses
      });

      setExpenses(updatedExpenses);
      setIsModalOpen(false);
      setEditingExpense(null);

      toast.success('Expense updated successfully');
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Failed to update expense');
    }
  };

  // Handle category filter
  const handleCategoryClick = (category) => {
    setCategoryFilter(currentFilter => 
      currentFilter === category ? null : category
    );
  };

  // Add this new function to handle sorting
  const getSortedExpenses = (expenses) => {
    if (!expenses) return [];
    
    const expensesCopy = [...expenses];
    
    switch (sortOption) {
      case 'currency-high':
        return expensesCopy.sort((a, b) => {
          // First sort by currency order
          const currencyOrderA = Object.keys(CURRENCY_SYMBOLS_TEXT).indexOf(a.currency);
          const currencyOrderB = Object.keys(CURRENCY_SYMBOLS_TEXT).indexOf(b.currency);
          if (currencyOrderA !== currencyOrderB) {
            return currencyOrderA - currencyOrderB;
          }
          // Then by amount (high to low)
          return parseFloat(b.amount) - parseFloat(a.amount);
        });

      case 'currency-low':
        return expensesCopy.sort((a, b) => {
          // First sort by currency order
          const currencyOrderA = Object.keys(CURRENCY_SYMBOLS_TEXT).indexOf(a.currency);
          const currencyOrderB = Object.keys(CURRENCY_SYMBOLS_TEXT).indexOf(b.currency);
          if (currencyOrderA !== currencyOrderB) {
            return currencyOrderA - currencyOrderB;
          }
          // Then by amount (low to high)
          return parseFloat(a.amount) - parseFloat(b.amount);
        });

      case 'title-asc':
        return expensesCopy.sort((a, b) => 
          a.title.toLowerCase().localeCompare(b.title.toLowerCase())
        );

      case 'title-desc':
        return expensesCopy.sort((a, b) => 
          b.title.toLowerCase().localeCompare(a.title.toLowerCase())
        );

      default:
        return expensesCopy;
    }
  };

  // Modify the filtered expenses to include recurring expenses for the selected date
  const filteredExpenses = React.useMemo(() => {
    if (activeTab === 'overview') return [];
    
    // Get regular expenses for this date
    const dateExpenses = expenses[activeTab] || [];
    
    // Get recurring expenses for this date
    const dateRecurring = recurringExpenses.filter(recurring => {
      if (recurring.isPaused) return false;
      
      // For one-off payments, only show on the exact date
      if (recurring.isOneOff) {
        return format(new Date(recurring.nextPaymentDate), 'yyyy-MM-dd') === activeTab;
      }
      
      const startDate = new Date(recurring.nextPaymentDate);
      startDate.setHours(0, 0, 0, 0); // Normalize to midnight
      const currentDate = new Date(activeTab);
      currentDate.setHours(0, 0, 0, 0); // Normalize to midnight
      const endDate = recurring.endDate ? new Date(recurring.endDate) : null;
      if (endDate) endDate.setHours(0, 0, 0, 0); // Normalize to midnight
      
      // If the current date is before the start date or after the end date, don't show it
      if (currentDate < startDate) return false;
      if (endDate && currentDate > endDate) return false;
      
      // Calculate if this date matches the recurring pattern
      const diffTime = currentDate.getTime() - startDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Normalize the period unit to handle both singular and plural forms
      const normalizedUnit = recurring.period?.unit?.toLowerCase().replace(/s$/, '');
      
      switch (normalizedUnit) {
        case 'day':
          return diffDays % parseInt(recurring.period.number) === 0;
        case 'week':
          return diffDays % (parseInt(recurring.period.number) * 7) === 0;
        case 'month':
          return currentDate.getDate() === startDate.getDate() &&
                 (currentDate.getMonth() - startDate.getMonth() + 
                  (currentDate.getFullYear() - startDate.getFullYear()) * 12) % 
                  parseInt(recurring.period.number) === 0;
        case 'year':
          return currentDate.getDate() === startDate.getDate() &&
                 currentDate.getMonth() === startDate.getMonth() &&
                 (currentDate.getFullYear() - startDate.getFullYear()) % 
                 parseInt(recurring.period.number) === 0;
        default:
          return false;
      }
    }).map(recurring => ({
      ...recurring,
      id: `${recurring.id}-${activeTab}`,
      isRecurring: true,
      color: stickyColors[Math.floor(Math.random() * stickyColors.length)]
    }));

    // Combine and filter by category if needed
    const allExpenses = [...dateExpenses, ...dateRecurring];
    const filtered = categoryFilter
      ? allExpenses.filter(expense => expense.category === categoryFilter)
      : allExpenses;

    // Apply sorting
    return getSortedExpenses(filtered);
  }, [activeTab, expenses, recurringExpenses, categoryFilter, sortOption]);

  // Modified addExpense to handle editing
  const addExpense = async (expenseData, closeModal) => {
    try {
      const dateKey = expenseData.date || format(new Date(), 'yyyy-MM-dd');
      const existingExpenses = expenses[dateKey] || [];
      
      const newExpense = {
        ...expenseData,
        id: Date.now().toString(),
        color: getNextAvailableColor(existingExpenses, stickyColors),
        createdAt: new Date().toISOString(),
      };

      // Update expenses state
      const updatedExpenses = {
        ...expenses,
        [dateKey]: [...(expenses[dateKey] || []), newExpense]
      };

      // Update Firebase
      await updateDoc(doc(db, 'expenses', currentUser.uid), {
        items: updatedExpenses
      });

      setExpenses(updatedExpenses);
      if (closeModal) closeModal();
      toast.success('Expense added successfully');
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  // Add this new function
  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setIsModalOpen(true);
  };

  // Update the duplicate function to use the new color distribution
  const handleDuplicateExpense = async (expense) => {
    try {
      let expenseToClone;
      let dateKey;

      if (expense.date) {
        // If the expense object already has a date (from data table)
        expenseToClone = expense;
        dateKey = expense.date;
      } else {
        // For sticky notes, find the expense in the current expenses state
        const foundDate = Object.entries(expenses).find(([date, dayExpenses]) => 
          dayExpenses.some(exp => exp.id === (typeof expense === 'string' ? expense : expense.id))
        )?.[0];

        if (!foundDate) {
          throw new Error('Expense not found');
        }

        dateKey = foundDate;
        expenseToClone = expenses[dateKey].find(exp => 
          exp.id === (typeof expense === 'string' ? expense : expense.id)
        );
      }

      if (!expenseToClone) {
        throw new Error('Expense not found');
      }

      const existingExpenses = expenses[dateKey] || [];

      // Create new expense object with distributed color and " - copy" added to title
      const newExpense = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        title: `${expenseToClone.title} - copy`,
        amount: expenseToClone.amount,
        currency: expenseToClone.currency,
        category: expenseToClone.category,
        description: expenseToClone.description || '',
        color: getNextAvailableColor(existingExpenses, stickyColors),
        createdAt: new Date().toISOString()
      };

      // Initialize the date array if it doesn't exist
      if (!expenses[dateKey]) {
        expenses[dateKey] = [];
      }

      const updatedExpenses = {
        ...expenses,
        [dateKey]: [...expenses[dateKey], newExpense]
      };

      // Update Firebase
      await setDoc(doc(db, 'expenses', currentUser.uid), {
        items: updatedExpenses
      });

      // Update local state
      setExpenses(updatedExpenses);
      toast.success('Expense duplicated successfully');
    } catch (error) {
      console.error('Error duplicating expense:', error);
      toast.error('Failed to duplicate expense');
    }
  };

  // Add these new functions for chart data preparation
  const prepareCategoryData = (expenses) => {
    if (!expenses) return [];
    
    return expenses.map(expense => ({
      name: expense.category,
      value: parseFloat(expense.amount),
      currency: expense.currency
    }));
  };

  const prepareLast7DaysData = () => {
    const last7Days = [];
    const currentDate = activeTab === 'overview' ? new Date() : parseISO(activeTab);
    
    for (let i = 6; i >= 0; i--) {
      const date = subDays(currentDate, i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayExpenses = expenses[dateStr] || [];
      
      // Group expenses by currency with proper rounding
      const currencyTotals = dayExpenses.reduce((acc, expense) => {
        const currency = expense.currency;
        if (!acc[currency]) {
          acc[currency] = 0;
        }
        // Round to 2 decimal places after each addition
        acc[currency] = Number((acc[currency] + parseFloat(expense.amount)).toFixed(2));
        return acc;
      }, {});

      last7Days.push({
        name: format(date, 'd MMM'),
        ...currencyTotals
      });
    }
    
    return {
      data: last7Days,
      currencies: [...new Set(Object.values(expenses)
        .flat()
        .map(expense => expense.currency))]
    };
  };

  // Add this function to prepare table data with current month
  const prepareTableData = () => {
    const firstDay = startOfMonth(currentDate);  // Use currentDate from CustomCalendar
    const lastDay = endOfMonth(currentDate);
    
    const monthlyExpenses = Object.entries(expenses)
      .filter(([date]) => {
        const expenseDate = parseISO(date);
        return expenseDate >= firstDay && expenseDate <= lastDay;
      })
      .flatMap(([date, dayExpenses]) => 
        dayExpenses.map(expense => ({
          ...expense,
          date: date
        }))
      );

    return monthlyExpenses;
  };

  // Add this helper function near the top of the component
  const calculateTotalExpenses = (expenses) => {
    const totals = {};
    expenses.forEach(expense => {
      if (!totals[expense.currency]) {
        totals[expense.currency] = 0;
      }
      totals[expense.currency] += parseFloat(expense.amount);
    });
    return Object.entries(totals)
      .map(([currency, amount]) => `${CURRENCY_SYMBOLS_TEXT[currency]}${amount.toFixed(2)}`)
      .join(', ');
  };

  // Modify the columns configuration
  const columns = React.useMemo(() => [
    {
      Header: 'Title',
      accessor: 'title',
      sortType: (rowA, rowB) => {
        return rowA.values.title.toLowerCase().localeCompare(
          rowB.values.title.toLowerCase()
        );
      },
      Cell: ({ row }) => (
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent sort trigger
            handleEditClick(row.original);
          }}
          className="text-indigo-600 hover:text-indigo-800 dark:text-orange-200"
        >
          <div className="truncate max-w-[300px] sm:max-w-[400px] md:max-w-[500px]" title={row.original.title}>
            {row.original.title}
          </div>
        </button>
      )
    },
    {
      Header: 'Category',
      accessor: 'category',
      sortType: 'alphanumeric'
    },
    {
      Header: 'Amount',
      accessor: row => ({ currency: row.currency, amount: parseFloat(row.amount) }),
      id: 'amount',
      sortType: (rowA, rowB) => {
        // First sort by currency
        const currencyCompare = rowA.values.amount.currency.localeCompare(rowB.values.amount.currency);
        if (currencyCompare !== 0) return currencyCompare;
        
        // Then sort by amount
        return rowA.values.amount.amount - rowB.values.amount.amount;
      },
      Cell: ({ value }) => (
        <span>{CURRENCY_SYMBOLS_TEXT[value.currency]}{value.amount.toFixed(2)}</span>
      )
    },
    {
      Header: 'Date',
      accessor: 'date',
      sortType: 'basic',
      sortMethod: (a, b) => {
        const dateA = parseISO(a);
        const dateB = parseISO(b);
        return dateA.getTime() - dateB.getTime();
      },
      Cell: ({ value }) => format(parseISO(value), 'dd MMM yyyy')
    },
    {
      Header: 'Actions',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => (
        <button
          onClick={() => handleDeleteClick(row.original)}
          className="text-red-500 hover:text-red-700"
        >
          <FaTrash />
        </button>
      )
    }
  ], []);

  // Update handleDeleteClick to work with both table and sticky notes
  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    setDeleteModalOpen(true);
  };

  const ListViewItem = ({ expense }) => {
    const isPending = expense.isRecurring;

    return (
      <div className={`
        ${expense.color} 
        rounded-lg p-2 sm:p-4 
        shadow-sm hover:shadow-md 
        transition-shadow 
        flex justify-between 
        items-start 
        text-sm sm:text-base
        ${isPending ? 'border-l-4 border-teal-500 dark:border-pink-500' : ''}
      `}>
        <div className="flex-1 min-w-0 mr-2">
          <div className="flex items-center gap-2">
            {isPending && (
              <span className="bg-teal-500 dark:bg-pink-500 text-white px-2 py-0.5 rounded text-xs font-semibold">
                Upcoming
              </span>
            )}
            <div className="font-medium dark:text-gray-800 truncate">{expense.title}</div>
          </div>
          <div className="flex flex-col gap-1 mt-1">
            <button 
              onClick={() => handleCategoryClick(expense.category)}
              className="inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-800 text-gray-600 dark:text-gray-700 text-xs sm:text-sm w-fit"
            >
              <FaTag size={10} className="sm:w-3 sm:h-3" />
              {expense.category}
            </button>
            {/* Add recurring info for pending items */}
            {isPending && (
              <div className="text-xs sm:text-sm text-teal-600 dark:text-pink-600">
                {expense.isOneOff ? (
                  `One-off payment on ${format(new Date(expense.nextPaymentDate), 'd MMM yyyy')}`
                ) : (
                  <>
                    Every {expense.period.number} {expense.period.unit}
                    {expense.endDate && (
                      <span className="ml-1">
                        • Ends {format(new Date(expense.endDate), 'd MMM yyyy')}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="font-semibold dark:text-gray-800 text-xs sm:text-sm whitespace-nowrap">
            {CURRENCY_SYMBOLS_TEXT[expense.currency]}{expense.amount}
          </div>
          {/* Only show action buttons for non-pending expenses */}
          {!isPending && (
            <div className="flex gap-1 sm:gap-2">
              <button 
                onClick={() => handleEditClick(expense)}
                className="p-1 sm:p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <FaPencilAlt className="w-3 h-3 sm:w-4 sm:h-4 text-teal-600" />
              </button>
              <button 
                onClick={() => handleDuplicateExpense(expense)}
                className="p-1 sm:p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <FaCopy className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </button>
              <button 
                onClick={() => handleDeleteClick(expense)}
                className="p-1 sm:p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <FaTrash className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Update the pending columns definition
  const pendingColumns = React.useMemo(() => [
    {
      Header: 'Title',
      accessor: 'title',
    },
    {
      Header: 'Amount',
      accessor: row => ({ amount: parseFloat(row.amount), currency: row.currency }),
      id: 'amount',
      sortType: (rowA, rowB) => {
        // First sort by currency
        const currencyCompare = rowA.values.amount.currency.localeCompare(rowB.values.amount.currency);
        if (currencyCompare !== 0) return currencyCompare;
        
        // Then sort by amount
        return rowA.values.amount.amount - rowB.values.amount.amount;
      },
      Cell: ({ value }) => `${CURRENCY_SYMBOLS_TEXT[value.currency]}${value.amount.toFixed(2)}`
    },
    {
      Header: 'Category',
      accessor: 'category',
    },
    {
      Header: 'Frequency',
      accessor: 'frequency'
    },
    {
      Header: 'Next Payment',
      accessor: 'nextPaymentFormatted'
    },
    {
      Header: 'End Date',
      accessor: 'endDate',
      Cell: ({ value }) => value ? format(new Date(value), 'dd MMM yyyy') : 'Ongoing'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium
          ${value === 'Due' 
            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
          }`}
        >
          {value}
        </span>
      )
    }
  ], []);

  const addBulkExpense = async (expensesArray, closeModal) => {
    try {
      // Clone the current expenses state
      const updatedExpenses = { ...expenses };

      for (const expenseData of expensesArray) {
        const dateKey = expenseData.date || format(new Date(), 'yyyy-MM-dd');
        const existingExpenses = updatedExpenses[dateKey] || [];

        const newExpense = {
          ...expenseData,
          id: Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9),
          color: getNextAvailableColor(existingExpenses, stickyColors),
          createdAt: new Date().toISOString(),
        };

        if (!updatedExpenses[dateKey]) {
          updatedExpenses[dateKey] = [];
        }
        updatedExpenses[dateKey].push(newExpense);
      }

      // Update Firestore
      await updateDoc(doc(db, 'expenses', currentUser.uid), {
        items: updatedExpenses
      });

      setExpenses(updatedExpenses);
      if (closeModal) closeModal();
      toast.success('Bulk expenses added successfully');
    } catch (error) {
      console.error('Error adding bulk expenses:', error);
      toast.error('Failed to add bulk expenses');
    }
  };

  const prepareCategorySummaryData = () => {
    const firstDay = startOfMonth(currentDate);
    const lastDay = endOfMonth(currentDate);
    
    // Get all expenses for the current month
    const monthlyExpenses = Object.entries(expenses)
      .filter(([date]) => {
        const expenseDate = parseISO(date);
        return expenseDate >= firstDay && expenseDate <= lastDay;
      })
      .flatMap(([date, dayExpenses]) => dayExpenses);

    // Group expenses by currency and category
    const categoryTotalsByCurrency = monthlyExpenses.reduce((acc, expense) => {
      if (!acc[expense.currency]) {
        acc[expense.currency] = {};
      }
      
      const key = expense.category;
      if (!acc[expense.currency][key]) {
        acc[expense.currency][key] = {
          category: expense.category,
          total: 0,
          count: 0
        };
      }
      
      acc[expense.currency][key].total += parseFloat(expense.amount);
      acc[expense.currency][key].count += 1;
      return acc;
    }, {});

    // Convert to formatted data for each currency
    const result = {};
    Object.entries(categoryTotalsByCurrency).forEach(([currency, categories]) => {
      const totalForCurrency = Object.values(categories)
        .reduce((sum, cat) => sum + cat.total, 0);
      
      result[currency] = Object.values(categories).map(item => ({
        category: item.category,
        amount: `${CURRENCY_SYMBOLS_TEXT[currency]}${item.total.toFixed(2)}`,
        transactions: item.count,
        percentage: ((item.total / totalForCurrency) * 100).toFixed(1)
      }));
    });

    return result;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="w-screen mx-auto py-6 px-4 md:w-5/6 md:p-6">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Monthly & Lifetime Expenses
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Monitor your spending, log payments, and manage your finances with ease.
          </p>
        </motion.div>

        {/* View Mode Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center mb-6"
        >
          <div className="relative inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1">
            <button
              onClick={() => setViewMode('monthly')}
              className="relative z-10 px-3 py-1.5 text-sm font-medium transition-colors"
            >
              <span className={`${viewMode === 'monthly' ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                Monthly
              </span>
            </button>
            <button
              onClick={() => setViewMode('all')}
              className="relative z-10 px-3 py-1.5 text-sm font-medium transition-colors"
            >
              <span className={`${viewMode === 'all' ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                Lifetime
              </span>
            </button>
            <motion.div
              className="absolute left-0 top-0 h-full bg-indigo-600 dark:bg-indigo-500 rounded-full"
              initial={false}
              animate={{
                width: viewMode === 'monthly' ? '50%' : '50%',
                x: viewMode === 'monthly' ? '0%' : '100%',
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          </div>
        </motion.div>

        {viewMode === 'all' ? (
          <AllTimeExpenseView 
            expenses={expenses}
            categories={categories}
            onEdit={handleEditExpense}
            onDelete={handleDeleteExpense}
            updateCategories={updateCategories}
            currencies={userCurrencies}
            onAddExpense={(data, closeModal) => {
              if (Array.isArray(data)) {
                addBulkExpense(data, closeModal);
              }
            }}
          />
        ) : (
          <>
            {/* Tabs */}
            <div className="flex bg-white rounded-t-lg border-b border-gray-200 overflow-x-auto h-12">
              {tabs.map(tab => (
                <div
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center px-4 py-2 cursor-pointer min-w-[120px] max-w-[150px]
                    ${activeTab === tab.id 
                      ? 'bg-indigo-50 text-indigo-600 border-b-2 border-indigo-600' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-500'}
                  `}
                >
                  <span className="truncate flex-1">{tab.title}</span>
                  {!tab.isOverview && (
                    <button
                      onClick={(e) => closeTab(tab.id, e)}
                      className="ml-4 text-gray-400 hover:text-gray-600"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Tab Content */}
            <div className="bg-white dark:bg-slate-900 rounded-b-lg shadow">
              {activeTab === 'overview' ? (
                <div className="p-6 space-y-6">
                  {/* Collapsible Calendar Section */}
                  <div className="border rounded-lg bg-gray-50">
                    <button
                      onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
                      className="w-full p-4 flex justify-between items-center text-left"
                    >
                      <h2 className="text-xl font-semibold">Calendar View</h2>
                      <svg
                        className={`w-6 h-6 transform transition-transform duration-300 ${
                          isCalendarExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div 
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        isCalendarExpanded ? 'max-h-[1200px] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="p-4 pt-0">
                        <CustomCalendar 
                          onDateClick={handleDateClick}
                          expenseData={expenses}
                          currentDate={currentDate}
                          setCurrentDate={setCurrentDate}
                          recurringExpenses={recurringExpenses}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Collapsible Table Section */}
                  <div className="border rounded-lg bg-gray-50">
                    <button
                      onClick={() => setIsTableExpanded(!isTableExpanded)}
                      className="w-full p-4 flex justify-between items-center text-left"
                    >
                      <div>
                        <h2 className="text-xl font-semibold">
                          {format(currentDate, 'MMMM yyyy')} Expenses
                        </h2>
                        {prepareTableData().length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">
                            Total: {calculateTotalExpenses(prepareTableData())}
                          </p>
                        )}
                      </div>
                      <svg
                        className={`w-6 h-6 transform transition-transform duration-300 ${
                          isTableExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div 
                      className={`transition-all duration-300 ease-in-out ${
                        isTableExpanded ? 'overflow-y-auto max-h-[900px] opacity-100' : 'overflow-hidden max-h-0 opacity-0'
                      }`}
                    >
                      <div className="p-4 pt-0">
                        <DataTableStructure
                          columns={columns}
                          data={prepareTableData()}
                          initialPageSize={10}
                          globalFilter={tableGlobalFilter}
                          setGlobalFilter={setTableGlobalFilter}
                          initialSortBy={expenseTableSortBy}
                          onSortChange={setExpenseTableSortBy}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Pending Expenses Table */}
                  <div className="border rounded-lg bg-gray-50">
                    <button
                      onClick={() => setIsPendingExpanded(!isPendingExpanded)}
                      className="w-full p-4 flex justify-between items-center text-left"
                    >
                      <div>
                        <h2 className="text-xl font-semibold">
                          {format(currentDate, 'MMMM yyyy')} Pending Amounts
                        </h2>
                        {preparePendingExpenses(recurringExpenses, currentDate).length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">
                            Upcoming: {calculateTotalExpenses(preparePendingExpenses(recurringExpenses, currentDate))}
                          </p>
                        )}
                      </div>
                      <svg
                        className={`w-6 h-6 transform transition-transform duration-300 ${
                          isPendingExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div 
                      className={`transition-all duration-300 ease-in-out ${
                        isPendingExpanded ? 'overflow-y-auto max-h-[900px] opacity-100' : 'overflow-hidden max-h-0 opacity-0'
                      }`}
                    >
                      <div className="p-4 pt-0">
                        <DataTableStructure
                          columns={pendingColumns}
                          data={preparePendingExpenses(recurringExpenses, currentDate)}
                          initialPageSize={10}
                          globalFilter={tableGlobalFilter}
                          setGlobalFilter={setTableGlobalFilter}
                          initialSortBy={pendingTableSortBy}
                          onSortChange={setPendingTableSortBy}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Category Summary Tables */}
                  <CategorySummaryTables 
                    categoryData={prepareCategorySummaryData()} 
                    title={`${format(currentDate, 'MMMM yyyy')} Expenses by Category`}
                    defaultCollapsed={true}
                  />
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex flex-col gap-4 max-w-48 md:max-w-none">
                      <div>
                        <h2 className="text-xl font-semibold">
                          Expenses for {tabs.find(tab => tab.id === activeTab)?.title}
                        </h2>
                        {filteredExpenses?.length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">
                            Total: {calculateTotalExpenses(filteredExpenses)}
                          </p>
                        )}
                        {categoryFilter && (
                          <div className="mt-2 flex items-center">
                            <span className="text-sm text-gray-500">
                              Filtered by category: 
                              <span className="font-medium text-indigo-600 ml-1">{categoryFilter}</span>
                              <button
                                onClick={() => setCategoryFilter(null)}
                                className="ml-2 text-gray-400 hover:text-gray-600"
                              >
                                <FaTimes size={12} />
                              </button>
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Sort controls */}
                      <div className="flex flex-col gap-4">
                        <select
                          value={sortOption}
                          onChange={(e) => setSortOption(e.target.value)}
                          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          <option value="default">Sort by</option>
                          <optgroup label="By Amount">
                            <option value="currency-high">Currency: Highest first</option>
                            <option value="currency-low">Currency: Lowest first</option>
                          </optgroup>
                          <optgroup label="Alphabetically">
                            <option value="title-asc">Title: A to Z</option>
                            <option value="title-desc">Title: Z to A</option>
                          </optgroup>
                        </select>

                        <div className="flex items-center gap-3">
                          <div className="flex items-center bg-teal-200 dark:bg-gray-50 rounded-lg p-1 w-max">
                            <button
                              onClick={() => setDisplayMode('list')}
                              className={`p-2 rounded-md transition-colors ${
                                displayMode === 'list' 
                                  ? 'bg-white shadow-sm' 
                                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-700 dark:hover:text-blue-500'
                              }`}
                            >
                              <FaList className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDisplayMode('grid')}
                              className={`p-2 rounded-md transition-colors ${
                                displayMode === 'grid' 
                                  ? 'bg-white shadow-sm' 
                                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-700 dark:hover:text-blue-500'
                              }`}
                            >
                              <FaTh className="w-4 h-4" />
                            </button>
                          </div>
                          <FaInfoCircle 
                            className="text-gray-500 hover:text-gray-800 dark:text-gray-100 dark:hover:text-gray-300 cursor-help w-5 h-5" 
                            data-tooltip-id="display-mode-tooltip"
                          />
                          <Tooltip
                            id="display-mode-tooltip"
                            place="right"
                            content="List view is more compact; Grid view shows descriptions."
                            className="z-50 dark:bg-gray-100 dark:text-gray-700"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setEditingExpense(null);
                        setIsModalOpen(true);
                      }}
                      className="bg-indigo-600 text-white w-24 md:w-40 px-2 py-2 rounded-lg hover:bg-indigo-700 shrink-0"
                    >
                      Add New Expense
                    </button>
                  </div>
              
                  {filteredExpenses?.length > 0 ? (
                    <div className={`
                      ${displayMode === 'grid' 
                        ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4'
                        : 'flex flex-col gap-2'
                      }
                    `}>
                      {filteredExpenses.map((expense) => (
                        displayMode === 'grid' ? (
                          <StickyNote
                            key={expense.id}
                            expense={expense}
                            onDelete={handleDeleteExpense}
                            onEdit={handleEditClick}
                            onCategoryClick={handleCategoryClick}
                            onDuplicate={handleDuplicateExpense}
                          />
                        ) : (
                          <ListViewItem
                            key={expense.id}
                            expense={expense}
                          />
                        )
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      {categoryFilter 
                        ? `No expenses found in category "${categoryFilter}"`
                        : activeTab === 'overview'
                          ? 'Select a date to view or add expenses'
                          : 'No expenses for this date. Click "Add New Expense" to get started.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Expense Modal */}
      {isModalOpen && (
        <ExpenseModal
          onClose={() => {
            setIsModalOpen(false);
            setEditingExpense(null);
          }}
          onSubmit={editingExpense 
            ? handleEditExpense 
            : (expenseData) => addExpense(expenseData, () => setIsModalOpen(false))}
          categories={categories}
          updateCategories={updateCategories}
          currencies={userCurrencies}
          editingExpense={editingExpense}
          selectedDate={new Date(activeTab === 'overview' ? Date.now() : activeTab)}
        />
      )}

      {activeTab !== 'overview' && (
        <div className="px-4 md:px-12 pt-2 pb-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <PieChartStructure
            data={prepareCategoryData(filteredExpenses)}
            title="Categories Breakdown"
          />
          <BarChartStructure
            data={prepareLast7DaysData().data}
            dataKeys={prepareLast7DaysData().currencies}
            title="Last 7 Days Expenses"
            xAxisLabel="Date"
            yAxisLabel="Amount"
            currencySymbol=""
          />
        </div>
      )}

      {/* Add the DeleteModal component */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setExpenseToDelete(null);
        }}
        onConfirm={() => handleDeleteExpense(expenseToDelete)}
        itemName={expenseToDelete?.title}
      />
    </div>
  );
};

export default Expense;
